import { ScaleElement } from '@scale/base-element';
import { Events } from '@scale/events';

class ProductMediaGallery extends ScaleElement {
  #currentIndex = 0;
  #slides = [];
  #thumbs = [];
  #indicators = [];
  #variantMediaMap = null;
  #allSlides = [];
  #allThumbs = [];
  #allIndicators = [];
  #slideObserver = null;
  #isScrollSnap = true;
  #scrollTimeout = null;

  setup() {
    this.#allSlides = [...this.querySelectorAll('.product-media-gallery__slide')];
    this.#slides = [...this.#allSlides];
    this.#allThumbs = [...this.querySelectorAll('.product-media-gallery__thumb')];
    this.#thumbs = [...this.#allThumbs];
    this.#allIndicators = [...this.querySelectorAll('.product-media-gallery__indicator')];
    this.#indicators = [...this.#allIndicators];

    const transition = this.dataset.transition || 'slide';
    this.#isScrollSnap = transition === 'slide' || transition === 'none';

    if (!this.#isScrollSnap) {
      this.classList.add(`product-media-gallery--${transition}`);
      if (this.#slides[0]) this.#slides[0].classList.add('is-active');
    }

    if (this.hasAttribute('data-hide-variant-images')) {
      this.#loadVariantMediaMap();
    }

    this.#setupZoom();

    if (this.#slides.length <= 1) return;

    this.#setupScrollTracking();
    this.#setupArrows();
    this.#setupThumbnails();
    this.#setupIndicators();
    this.#setupKeyboard();
    this.#setupVariantSync();
    this.#setupAdaptiveHeight();
    this.#syncUI();

    // Design mode: scroll to selected block
    if (Shopify?.designMode) {
      this.onDocument('shopify:block:select', (e) => {
        if (e.detail.blockId === this.closest('[id]')?.id?.replace('shopify-block-', '')) {
          this.#goTo(0);
        }
      });
    }
  }

  teardown() {
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
  }

  // ── Scroll tracking (IntersectionObserver) ──

  #setupScrollTracking() {
    if (!this.#isScrollSnap) return;

    const track = this.ref('track');
    if (!track) return;

    this.#slideObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = this.#slides.indexOf(entry.target);
            if (index >= 0 && index !== this.#currentIndex) {
              this.#currentIndex = index;
              this.#syncUI();
            }
          }
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const slide of this.#slides) {
      this.#slideObserver.observe(slide);
    }

    // Update arrows on scroll end
    this.on('scroll', () => {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = setTimeout(() => this.#updateArrows(), 100);
    }, { target: track, passive: true });
  }

  // ── Navigation ──

  #setupArrows() {
    const prev = this.ref('prev');
    const next = this.ref('next');
    if (prev) this.on('click', () => this.#goTo(this.#currentIndex - 1), { target: prev });
    if (next) this.on('click', () => this.#goTo(this.#currentIndex + 1), { target: next });
  }

  #goTo(index) {
    if (index < 0 || index >= this.#slides.length) return;

    if (this.#isScrollSnap) {
      const slide = this.#slides[index];
      if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    } else {
      // Fade / zoom transitions
      this.#slides[this.#currentIndex]?.classList.remove('is-active');
      this.#slides[index]?.classList.add('is-active');
    }

    this.#currentIndex = index;
    this.#syncUI();
  }

  #goToMedia(mediaId) {
    const id = String(mediaId);
    const index = this.#slides.findIndex((s) => s.dataset.mediaId === id);
    if (index >= 0) this.#goTo(index);
  }

  // ── UI sync ──

  #syncUI() {
    this.#updateThumbs();
    this.#updateIndicators();
    this.#updateArrows();
    this.#handleMediaPlayback();
    this.#updateTrackHeight();

    const label = `${this.#currentIndex + 1} / ${this.#slides.length}`;
    const counter = this.ref('counter');
    if (counter) counter.textContent = label;
    const liveRegion = this.ref('live-region');
    if (liveRegion) liveRegion.textContent = label;

    for (let i = 0; i < this.#slides.length; i++) {
      this.#slides[i].setAttribute('aria-hidden', String(i !== this.#currentIndex));
    }
  }

  // ── Adaptive height (image_ratio: adapt) ──

  #setupAdaptiveHeight() {
    const firstMedia = this.#slides[0]?.querySelector('.product-media');
    if (!firstMedia?.classList.contains('product-media--adapt')) return;

    for (const slide of this.#allSlides) {
      const img = slide.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', () => this.#updateTrackHeight(), { once: true });
      }
    }

    this.on('resize', () => this.debounce('track-height', () => this.#updateTrackHeight(), 100), { target: window, passive: true });
  }

  #updateTrackHeight() {
    const track = this.ref('track');
    if (!track) return;

    if (this.dataset.mobileLayout === 'stack' && window.matchMedia('(width < 750px)').matches) {
      track.style.height = '';
      return;
    }

    const firstMedia = this.#slides[0]?.querySelector('.product-media');
    if (!firstMedia?.classList.contains('product-media--adapt')) return;

    const active = this.#slides[this.#currentIndex];
    if (!active) return;

    const height = active.getBoundingClientRect().height;
    if (height > 0) track.style.height = `${height}px`;
  }

  // ── Video pause/play on slide change ──

  #handleMediaPlayback() {
    for (let i = 0; i < this.#slides.length; i++) {
      const video = this.#slides[i].querySelector('video');
      if (!video) continue;
      if (i === this.#currentIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  #updateThumbs() {
    for (const thumb of this.#allThumbs) {
      thumb.classList.remove('is-active');
      thumb.removeAttribute('aria-current');
    }
    for (let i = 0; i < this.#thumbs.length; i++) {
      const isActive = i === this.#currentIndex;
      this.#thumbs[i].classList.toggle('is-active', isActive);
      if (isActive) this.#thumbs[i].setAttribute('aria-current', 'true');
    }
    this.#scrollActiveThumbIntoView();
  }

  // Scroll the active thumbnail into view WITHIN the thumbnails strip only.
  // Never use element.scrollIntoView(): it scrolls every scrollable ancestor
  // including the window, which yanks the page down when the gallery sits
  // below the fold (e.g. a featured product on the home page).
  #scrollActiveThumbIntoView() {
    const active = this.#thumbs[this.#currentIndex];
    const container = this.ref('thumbnails');
    if (!active || !container) return;

    const isVertical = container.scrollHeight > container.clientHeight;
    const isHorizontal = container.scrollWidth > container.clientWidth;
    if (!isVertical && !isHorizontal) return;

    // Position relative to the container via rects (independent of offsetParent),
    // then add the current scroll offset to get an absolute scroll target.
    const thumbRect = active.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (isVertical) {
      const offset = thumbRect.top - containerRect.top + container.scrollTop;
      const target = offset - (container.clientHeight - thumbRect.height) / 2;
      container.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      const offset = thumbRect.left - containerRect.left + container.scrollLeft;
      const target = offset - (container.clientWidth - thumbRect.width) / 2;
      container.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  #updateIndicators() {
    for (const indicator of this.#allIndicators) {
      indicator.classList.remove('is-active');
      indicator.removeAttribute('aria-current');
    }
    for (let i = 0; i < this.#indicators.length; i++) {
      const isActive = i === this.#currentIndex;
      this.#indicators[i].classList.toggle('is-active', isActive);
      if (isActive) this.#indicators[i].setAttribute('aria-current', 'true');
    }
  }

  #updateArrows() {
    const prev = this.ref('prev');
    const next = this.ref('next');
    if (prev) prev.disabled = this.#currentIndex === 0;
    if (next) next.disabled = this.#currentIndex === this.#slides.length - 1;
  }

  // ── Thumbnails ──

  #setupThumbnails() {
    for (const thumb of this.#thumbs) {
      this.on('click', () => {
        const visibleIndex = this.#slides.findIndex(
          (s) => s.dataset.mediaId === thumb.dataset.mediaId
        );
        if (visibleIndex >= 0) this.#goTo(visibleIndex);
      }, { target: thumb });
    }

    this.#setupThumbsArrows();
  }

  #setupThumbsArrows() {
    const container = this.ref('thumbnails');
    const prev = this.ref('thumbs-prev');
    const next = this.ref('thumbs-next');
    if (!container || !prev || !next || this.#thumbs.length <= 1) return;

    const scrollAmount = 200;

    this.on('click', () => {
      const isVertical = container.scrollHeight > container.clientHeight;
      container.scrollBy({ left: isVertical ? 0 : -scrollAmount, top: isVertical ? -scrollAmount : 0, behavior: 'smooth' });
    }, { target: prev });

    this.on('click', () => {
      const isVertical = container.scrollHeight > container.clientHeight;
      container.scrollBy({ left: isVertical ? 0 : scrollAmount, top: isVertical ? scrollAmount : 0, behavior: 'smooth' });
    }, { target: next });

    // Show/hide arrows based on scroll position
    const updateThumbsArrows = () => {
      const isHorizontal = container.scrollWidth > container.clientWidth;
      const isVertical = container.scrollHeight > container.clientHeight;

      if (isHorizontal) {
        prev.hidden = container.scrollLeft <= 1;
        next.hidden = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      } else if (isVertical) {
        prev.hidden = container.scrollTop <= 1;
        next.hidden = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
      } else {
        prev.hidden = true;
        next.hidden = true;
      }
    };

    this.on('scroll', () => this.debounce('thumbs-scroll', updateThumbsArrows, 50), { target: container, passive: true });
    updateThumbsArrows();
  }

  // ── Indicators (dots/bars) ──

  #setupIndicators() {
    for (const indicator of this.#indicators) {
      this.on('click', () => {
        const allIndex = Number(indicator.dataset.index);
        const slide = this.#allSlides[allIndex];
        const visibleIndex = this.#slides.indexOf(slide);
        if (visibleIndex >= 0) this.#goTo(visibleIndex);
      }, { target: indicator });
    }
  }

  // ── Keyboard ──

  #setupKeyboard() {
    this.on('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.#goTo(this.#currentIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#goTo(this.#currentIndex + 1); }
    });
    if (!this.getAttribute('tabindex')) this.setAttribute('tabindex', '0');
  }

  // ── Variant sync ──

  #setupVariantSync() {
    this.onDocument(Events.VARIANT_SELECTED, (e) => {
      if (!e.variant) return;
      if (this.#variantMediaMap) this.#filterMediaForVariant(e.variant);
      if (e.variant.featured_media?.id) this.#goToMedia(e.variant.featured_media.id);
    });

    // Media preview on swatch hover
    this.onDocument('scale:media:preview', (e) => {
      if (e.detail?.mediaId) this.#goToMedia(e.detail.mediaId);
    });
  }

  // ── Inline zoom ──

  #setupZoom() {
    for (const trigger of this.querySelectorAll('[data-zoom-trigger]')) {
      this.on('click', (e) => this.#toggleZoom(trigger, e), { target: trigger });
    }
  }

  #toggleZoom(trigger, e) {
    if (trigger.classList.contains('is-zoomed')) {
      trigger.classList.remove('is-zoomed');
      trigger.style.removeProperty('--zoom-x');
      trigger.style.removeProperty('--zoom-y');
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    trigger.style.setProperty('--zoom-x', `${x}%`);
    trigger.style.setProperty('--zoom-y', `${y}%`);
    trigger.classList.add('is-zoomed');
  }

  // ── Variant filtering ──

  #loadVariantMediaMap() {
    const json = this.ref('variant-media-json');
    if (!json) return;
    try {
      this.#variantMediaMap = JSON.parse(json.textContent);
    } catch { /* skip */ }
  }

  #filterMediaForVariant(variant) {
    const selectedMediaId = variant.featured_media?.id ? String(variant.featured_media.id) : null;

    const otherVariantMediaIds = new Set();
    for (const mediaId of Object.values(this.#variantMediaMap)) {
      const id = String(mediaId);
      if (id !== selectedMediaId) otherVariantMediaIds.add(id);
    }

    for (const slide of this.#allSlides) {
      slide.classList.toggle('is-hidden', otherVariantMediaIds.has(slide.dataset.mediaId));
    }
    for (const thumb of this.#allThumbs) {
      thumb.classList.toggle('is-hidden', otherVariantMediaIds.has(thumb.dataset.mediaId));
    }
    for (const indicator of this.#allIndicators) {
      const slide = this.#allSlides[Number(indicator.dataset.index)];
      if (slide) indicator.classList.toggle('is-hidden', otherVariantMediaIds.has(slide.dataset.mediaId));
    }

    this.#rebuildVisibleIndexes();
  }

  #rebuildVisibleIndexes() {
    this.#slides = this.#allSlides.filter((s) => !s.classList.contains('is-hidden'));
    this.#thumbs = this.#allThumbs.filter((t) => !t.classList.contains('is-hidden'));
    this.#indicators = this.#allIndicators.filter((i) => !i.classList.contains('is-hidden'));

    // Re-observe visible slides
    if (this.#slideObserver) {
      this.#slideObserver.disconnect();
      for (const slide of this.#slides) this.#slideObserver.observe(slide);
    }

    // Reset to first visible slide if current is hidden
    const currentSlide = this.#allSlides.find((s) => s.classList.contains('is-active'));
    if (currentSlide?.classList.contains('is-hidden') || !this.#slides.includes(this.#allSlides[this.#currentIndex])) {
      this.#currentIndex = 0;
      this.#goTo(0);
    } else {
      this.#currentIndex = this.#slides.indexOf(this.#allSlides[this.#currentIndex]) || 0;
    }

    this.#syncUI();
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
